import {useCallback, useEffect, useRef} from "react";
import {useGameStore} from "@/state/gameStore";
import {OverlayType} from "@/types/state";
import {DELAY_UPDATE_INTERVAL} from "@/config/timerConfig";

export function useMessageQueueTimer(): void {
    const isActive = useGameStore((state) => state.queueTimerActive);
    const duration = useGameStore((state) => state.queueTimerDuration);
    const activeOverlay = useGameStore((state) => state.activeOverlay);

    const updateQueueTimerElapsed = useGameStore((state) => state.updateQueueTimerElapsed);
    const endQueueTimer = useGameStore((state) => state.endQueueTimer);

    const intervalRef = useRef<number | null>(null);
    const startTimeRef = useRef<number>(0);
    const pausedElapsedRef = useRef<number>(0);

    const isPaused = activeOverlay !== OverlayType.None;

    const clearTimer = useCallback(() => {
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!isActive) {
            clearTimer();
            return;
        }

        if (isPaused) {
            if (intervalRef.current !== null) {
                pausedElapsedRef.current = Date.now() - startTimeRef.current;
                clearTimer();
            }
            return;
        }

        // Fresh timer start (not resuming from pause)
        if (pausedElapsedRef.current === 0) {
            startTimeRef.current = Date.now();
        } else {
            startTimeRef.current = Date.now() - pausedElapsedRef.current;
        }

        intervalRef.current = window.setInterval(() => {
            const elapsed = Date.now() - startTimeRef.current;

            if (elapsed >= duration) {
                clearTimer();
                endQueueTimer();
            } else {
                updateQueueTimerElapsed(elapsed);
            }
        }, DELAY_UPDATE_INTERVAL);

        return () => {
            clearTimer();
        };
    }, [isActive, isPaused, duration, clearTimer, updateQueueTimerElapsed, endQueueTimer]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, [clearTimer]);
}